import type { ReactNode } from "react";
import { formatNumberDigits, isArabicLanguage, toArabicIndicDigitsInString } from "./numberFormat";

const LARGE_NUMBER_TOKEN_REGEX = /(?<![\d.])(-?\d{4,})(\.\d+)?(?![\d.])/g;

/**
 * KPI trend percentage, e.g. `+12.2%` / `-١٣.٢%`.
 *
 * Deliberately *not* wrapped in a bidi isolate. A trailing `%` is an ET, which the bidi algorithm
 * binds to Western (EN) digits but not to Eastern Arabic-Indic (AN) ones — so in Arabic it resolves
 * against the RTL paragraph and renders to the left of the digits (`%١٣.٢`), which is the
 * convention. Isolating the token would force LTR order and put the `%` back on the right, hard
 * against the trend arrow. This matches how the Sales & Products cards render the backend's
 * `percent_changeFormattedValue` verbatim.
 */
export function formatChangePercentDisplay(value: number, language: string): string {
  const magnitude = formatNumberDigits(
    Math.abs(value),
    { minimumFractionDigits: value === 0 ? 1 : 0, maximumFractionDigits: 2 },
    "en"
  );
  const sign = value > 0 ? "+" : value < 0 ? "-" : "";
  const display = `${sign}${magnitude}%`;
  return isArabicLanguage(language) ? toArabicIndicDigitsInString(display) : display;
}

/**
 * Converts an API duration value (fractional minutes) to total seconds.
 * The backend now sends values in minutes, e.g. 3.68 = 3 min 41 sec.
 */
export function decodeApiDuration(value: number): number {
  if (!Number.isFinite(value) || value < 0) {
    return 0;
  }
  return Math.round(value * 60);
}

/** Unit words for a "N minutes M seconds" duration, so callers can supply localized copy. */
export interface DurationUnitLabels {
  minute: string;
  minutes: string;
  second: string;
  seconds: string;
}

const EN_DURATION_UNIT_LABELS: DurationUnitLabels = {
  minute: "minute",
  minutes: "minutes",
  second: "second",
  seconds: "seconds",
};

const formatDurationCount = (count: number) => String(count);

export function formatDurationSeconds(
  value: number,
  labels: DurationUnitLabels = EN_DURATION_UNIT_LABELS,
  formatCount: (_count: number) => string = formatDurationCount
): string {
  if (!Number.isFinite(value)) {
    return "-";
  }

  const totalSeconds = Math.max(0, Math.round(value));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const secondLabel = `${formatCount(seconds)} ${seconds === 1 ? labels.second : labels.seconds}`;

  if (minutes === 0) {
    return secondLabel;
  }

  const minuteLabel = `${formatCount(minutes)} ${minutes === 1 ? labels.minute : labels.minutes}`;
  return `${minuteLabel} ${secondLabel}`;
}

export function formatDashboardValueString(value: string): string {
  return value.replace(LARGE_NUMBER_TOKEN_REGEX, (_match, integerPart: string, decimalPart?: string) => {
    const normalizedInteger = Number(integerPart).toLocaleString();
    return decimalPart ? `${normalizedInteger}${decimalPart}` : normalizedInteger;
  });
}

export function formatDashboardValueNode(value: ReactNode): ReactNode {
  if (typeof value === "string") {
    return formatDashboardValueString(value);
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value.toLocaleString() : value;
  }

  return value;
}
